import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import AuthGuard from '../components/AuthGuard';
import { api } from '../lib/api';

const purposeOptions = ['资产证明', '贷款申请', '签证移民', '商业合作', '其他'];

export default function TaxCertificate() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [idCard, setIdCard] = useState('');
  const [phone, setPhone] = useState('');
  const [purpose, setPurpose] = useState('');
  const [receiveMethod] = useState('电子版+纸质版（邮寄）');
  const [address, setAddress] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [statusText, setStatusText] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/asset-restore/status'),
      api.get('/tax-certificate/status'),
    ]).then(([assetRes, taxRes]) => {
      if (!assetRes.data.has_applied) {
        navigate('/asset-restore', { replace: true });
        return;
      }
      if (taxRes.data.has_applied) {
        setHasApplied(true);
        const app = taxRes.data.application;
        if (app) {
          setName(app.name || '');
          setIdCard(app.id_number || '');
          setPhone(app.phone || '');
          setPurpose(app.purpose || '');
          setAddress(app.mailing_address || '');
          setAgreed(!!app.agreed);
        }
        const s = app?.status === 'approved' ? '已通过' : app?.status === 'rejected' ? '已拒绝' : '审核中';
        setStatusText(s);
      }
    }).catch(() => { });
  }, []);

  async function handleSubmit() {
    if (hasApplied || submitting) return;
    if (!name.trim() || !idCard.trim() || !phone.trim() || !purpose || !address.trim()) {
      toast.error('请填写完整信息');
      return;
    }
    if (!agreed) {
      toast.error('请先阅读并同意相关协议');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/tax-certificate', {
        name: name.trim(),
        id_number: idCard.trim(),
        phone: phone.trim(),
        purpose,
        delivery_method: receiveMethod,
        mailing_address: address.trim(),
        agreed,
      });
      toast.success('提交成功');
      setHasApplied(true);
      setStatusText('审核中');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || '提交失败');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-md mx-auto min-h-screen" style={{ backgroundColor: '#fefcfb' }}>
      <AuthGuard />

      {/* Hero */}
      <div className="relative px-4">
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/90 text-black/80"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
      </div>

      <div className="relative">
        <img src="/images/taxincome_shang.png" alt="完税证明" className="w-full" />
      </div>

      <div
        className="mt-[-10px] bg-white pl-[15px] pr-[12px] w-full pb-5"
      >
        <div
          className="pt-[10px] pb-[10px] overflow-hidden relative  shadow-[0_0_8px_rgba(196,22,28,0.15)] rounded-xl"
          style={{ backgroundImage: 'url(/images/taxincome_kuang.png)', backgroundSize: '100% 100%', backgroundPosition: 'top center', backgroundRepeat: 'no-repeat' }}
        >
          <div className="relative">
            {/* Section: 申请人信息 */}
            <div className="pt-[15px]">
              <div className="divide-y divide-[#fcf7f8] relative z-10">
                {/* Name */}
                <div className="flex items-center px-4 py-1.5 gap-3">
                  <img src="/images/taxincome_xingming.png" alt="" className="w-4 h-4 flex-shrink-0" />
                  <span className="text-xs font-medium text-gray-700 w-20 flex-shrink-0">申请人姓名</span>
                  <input
                    type="text"
                    placeholder="请输入真实姓名"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    readOnly={hasApplied}
                    className={`flex-1 text-xs text-right placeholder-gray-300 outline-none bg-transparent mr-1 ${hasApplied ? 'text-gray-800 font-medium' : 'text-gray-800'}`}
                  />
                </div>

                {/* ID Card */}
                <div className="flex items-center px-4 py-1.5 gap-3">
                  <img src="/images/taxincome_shenfenzheng.png" alt="" className="w-4 h-4 flex-shrink-0" />
                  <span className="text-xs font-medium text-gray-700 w-20 flex-shrink-0">身份证号码</span>
                  <input
                    type="text"
                    placeholder="请输入身份证号码"
                    value={idCard}
                    onChange={e => setIdCard(e.target.value)}
                    readOnly={hasApplied}
                    className={`flex-1 text-xs text-right placeholder-gray-300 outline-none bg-transparent mr-1 ${hasApplied ? 'text-gray-800 font-medium' : 'text-gray-800'}`}
                  />
                </div>

                {/* Phone */}
                <div className="flex items-center px-4 py-1.5 gap-3">
                  <img src="/images/taxincome_dianhua.png" alt="" className="w-4 h-4 flex-shrink-0" />
                  <span className="text-xs font-medium text-gray-700 w-20 flex-shrink-0">联系电话</span>
                  <input
                    type="tel"
                    placeholder="请输入联系电话"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    readOnly={hasApplied}
                    className={`flex-1 text-xs text-right placeholder-gray-300 outline-none bg-transparent mr-1 ${hasApplied ? 'text-gray-800 font-medium' : 'text-gray-800'}`}
                  />
                </div>

                {/* Purpose */}
                <div className="flex items-center px-4 py-1.5 gap-3">
                  <img src="/images/taxincome_yongtu.png" alt="" className="w-4 h-4 flex-shrink-0" />
                  <span className="text-xs font-medium text-gray-700 w-20 flex-shrink-0">领取用途</span>
                  <select
                    value={purpose}
                    onChange={e => setPurpose(e.target.value)}
                    disabled={hasApplied}
                    className={`flex-1 text-xs text-right placeholder-gray-300 outline-none bg-transparent ${hasApplied ? 'text-gray-800 font-medium appearance-none' : 'text-gray-800'}`}
                  >
                    <option value="">请选择领取用途</option>
                    {purposeOptions.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                {/* Receive method */}
                <div className="flex items-center px-4 py-1.5 gap-3">
                  <img src="/images/taxincome_jieshou.png" alt="" className="w-4 h-4 flex-shrink-0" />
                  <span className="text-xs font-medium text-gray-700 w-20 flex-shrink-0">接收方式</span>
                  <div className="flex-1 flex justify-end items-center gap-1">
                    <div className="text-right text-xs text-gray-800">{receiveMethod}</div>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-center px-4 py-1.5 gap-3">
                  <img src="/images/taxincome_dizhi.png" alt="" className="w-4 h-4 flex-shrink-0" />
                  <span className="text-xs font-medium text-gray-700 w-20 flex-shrink-0">邮寄地址</span>
                  <input
                    type="text"
                    placeholder="请输入邮寄地址"
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                    readOnly={hasApplied}
                    className={`flex-1 text-xs text-right placeholder-gray-300 outline-none bg-transparent mr-1 ${hasApplied ? 'text-gray-800 font-medium' : 'text-gray-800'}`}
                  />
                </div>

                <div className="flex items-center px-4 py-1.5 gap-3">

                  {/* Agreement */}
                  <div className="flex items-start gap-2 px-1">
                    <button
                      onClick={() => !hasApplied && setAgreed(!agreed)}
                      className={`mt-0.5 w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition ${agreed ? 'bg-[#c4161c] border-[#c4161c]' : 'border-[#c4161c]/20 bg-white'}`}
                    >
                      {agreed && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>}
                    </button>
                    <p className="text-[10px] text-gray-500 leading-relaxed">
                      我已阅读并同意<span className="text-[#c4161c]">《完税证明申请须知》</span><span className="text-[#c4161c]">《用户服务协议》</span>
                    </p>
                  </div>

                </div>
              </div>
            </div>

            {/* Submit / Status */}
            {hasApplied ? (
              <div className="text-center py-3">
                <div className="inline-block bg-[#fef2f2] text-[#c4161c] font-bold text-sm px-6 py-3 rounded-full">
                  已提交申请（{statusText}）
                </div>
              </div>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full mt-3"
              >
                <img src={"/images/taxincome_dianji.png"} alt="submit" className="w-[330px] mx-auto" />
              </button>
            )}
          </div>
        </div>

        <div className="my-3 shadow-[0_0_8px_rgba(196,22,28,0.15)] rounded-xl">
          <img src={"/images/taxincome_baohu.png"} alt="taxincome_baohu" className="w-full" />
        </div>
      </div>
    </div >
  );
}
